import { designVariables } from "@studio3t/design-variables";
import { Icon, IconSVG } from "@studio3t/generic-components";
import { useNavigate } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import pipelineArt from "@/assets/pipeline-3d.webp";
import { DOCS_URL } from "./dashboardCards";

const SPACING = designVariables.ui.semantic;

// The dashboard with no pipelines yet (Figma 15376-148247): the hero
// gradient holds a single onboarding card — the first-pipeline recipe
// (services → Studio → deploy) with the docs link and an Add service
// start. Sizes and offsets are the Figma frame's own: 700×420 card, 60px
// inner padding, the 14px frosted border over the gradient, the pipeline
// artwork bleeding off the card's lower right at 15% opacity.

export function DashboardEmptyState() {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Card>
      <Art alt="" src={pipelineArt} />
      <Content>
        <TitleRow>
          <IconTile>
            <IconSVG
              icon={Icon.Trace}
              size={26}
              colors={{ default: theme.semanticTextHeading }}
            />
          </IconTile>
          <Title>Create your first pipeline</Title>
        </TitleRow>
        <Indented>
          <Body>
            Add the services your pipeline needs first. Build your pipeline in
            Studio; when you are satisfied with the design, deploy it and keep
            an eye on its status.
          </Body>
          <LearnMore href={DOCS_URL} target="_blank" rel="noopener noreferrer">
            <IconSVG
              icon={Icon.Link}
              size={16}
              colors={{ default: theme.linkColor }}
            />
            Learn more
          </LearnMore>
        </Indented>
      </Content>
      <Actions>
        <GradientButton
          type="button"
          onClick={() => navigate("/services/new")}
          data-testid="dashboard-add-service"
        >
          <IconSVG
            icon={Icon.Plus}
            size={designVariables.ui.button.iconsize}
            colors={{ default: theme.semanticTextBody }}
          />
          Add service
        </GradientButton>
      </Actions>
    </Card>
  );
}

// background-clip keeps the white fill inside the 14px translucent
// border, so the hero gradient frosts through it like the mock.
// border-box: the 700×420 frame includes the border and padding (there is
// no global box-sizing reset to rely on).
const Card = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 700px;
  max-width: 100%;
  min-height: 420px;
  padding: 60px;
  border: 14px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  background: ${(p) => p.theme.semanticAppBackground};
  background-clip: padding-box;
  overflow: hidden;
  box-shadow:
    0 7px 33px rgba(17, 17, 17, 0.1),
    0 2px 6px rgba(17, 17, 17, 0.1);
`;

// The artwork's Figma placement, resolved to the card box: 837×523,
// bleeding 198px past the right edge and 46px below the bottom.
const Art = styled.img`
  position: absolute;
  right: -198px;
  bottom: -46px;
  width: 837px;
  height: 523px;
  object-fit: cover;
  opacity: 0.15;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.spacingm}px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.spacingm}px;
`;

const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 5px;
  background-image: linear-gradient(
    -42deg,
    rgba(246, 142, 52, 0.25) 8.5%,
    rgba(184, 209, 255, 0.25) 94.6%
  );
`;

const Title = styled.h2`
  margin: 0;
  font-size: 26px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
`;

// The copy column sits a full inner padding in from the title (Figma's
// 60px block indent).
const Indented = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.spacingl}px;
  padding: 0 60px;
`;

const Body = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextBody};
`;

const LearnMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${(p) => p.theme.linkColor};
  text-decoration: underline;

  &:hover {
    color: ${(p) => p.theme.linkColorHover};
  }
`;

const Actions = styled.div`
  position: relative;
  padding-left: 60px;
`;

// The special Add service face (Figma 15376-148262): the M subtle button
// wearing the hero's brand gradient — the fill at 25% alpha (the icon
// tile's wash) under a full-strength gradient hairline. Two stacked
// backgrounds paint it: the wash clipped to the padding box, the border
// gradient to the border box behind a transparent border.
const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

const GradientButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.m.size}px;
  padding: 0 ${BUTTON.m.paddingw}px;
  border: 1px solid transparent;
  border-radius: ${BUTTON.corner}px;
  /* Wash over an opaque white sheet (both padding-box) so the
     full-strength border gradient only survives in the 1px ring. */
  background:
    linear-gradient(
        -42deg,
        rgba(246, 142, 52, 0.25) 8.5%,
        rgba(184, 209, 255, 0.25) 94.6%
      )
      padding-box,
    linear-gradient(
        ${(p) => p.theme.semanticAppBackground},
        ${(p) => p.theme.semanticAppBackground}
      )
      padding-box,
    linear-gradient(-42deg, #f68e34 8.5%, #b8d1ff 94.6%) border-box;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextBody};
  cursor: pointer;

  &:hover {
    background:
      linear-gradient(
          -42deg,
          rgba(246, 142, 52, 0.35) 8.5%,
          rgba(184, 209, 255, 0.35) 94.6%
        )
        padding-box,
      linear-gradient(
          ${(p) => p.theme.semanticAppBackground},
          ${(p) => p.theme.semanticAppBackground}
        )
        padding-box,
      linear-gradient(-42deg, #f68e34 8.5%, #b8d1ff 94.6%) border-box;
  }
`;
